/**
 * Lógica pura de las hojas del Plan del bloque B6 (H35, H36, H37, H43):
 * menú de grupo y de mes, nueva categoría, reservar y resumen del mes.
 */

/** Largo máximo del avance de las notas en la fila «Notas». */
const LARGO_VISTA_NOTAS = 60;

/**
 * Avance de las notas para la fila «Notas» de la hoja de acciones (H35):
 * la primera línea con texto, recortada. Sin notas (o solo espacios), null:
 * la fila pasa a ser «Añadir nota».
 */
export function vistaNotas(notas: string | null | undefined): string | null {
  if (!notas) {
    return null;
  }
  const linea = notas
    .split('\n')
    .map(l => l.replace(/^[#>*\-\s]+/, '').trim())
    .find(l => l.length > 0);
  if (!linea) {
    return null;
  }
  return linea.length > LARGO_VISTA_NOTAS
    ? `${linea.slice(0, LARGO_VISTA_NOTAS - 1).trimEnd()}…`
    : linea;
}

/** Acciones del menú del mes (las de Actual, sin cambiar qué hacen). */
export type AccionMes =
  | 'copy-last'
  | 'set-zero'
  | 'set-3-avg'
  | 'set-6-avg'
  | 'set-12-avg'
  | 'check-templates'
  | 'apply-goal-template'
  | 'overwrite-goal-template'
  | 'cleanup-goal-template';

/**
 * Acciones del mes en el orden en que se ven. Las de plantillas solo con
 * las plantillas activadas.
 */
export function accionesMes(plantillas: boolean): AccionMes[] {
  const base: AccionMes[] = [
    'copy-last',
    'set-zero',
    'set-3-avg',
    'set-6-avg',
    'set-12-avg',
  ];
  if (!plantillas) {
    return base;
  }
  return [
    ...base,
    'check-templates',
    'apply-goal-template',
    'overwrite-goal-template',
    'cleanup-goal-template',
  ];
}

/** Nombre válido para una categoría o grupo nuevo (no vacío). */
export function nombreValido(nombre: string): boolean {
  return nombre.trim().length > 0;
}

/** Valores de la hoja del mes (céntimos, tal como los da la hoja de cálculo). */
export type ValoresResumen = {
  /** Ingresos del mes (`total-income`). */
  ingresos: number;
  /** Lo que sobró sin asignar el mes pasado (`from-last-month`). */
  delMesPasado: number;
  /** Gastado de más el mes pasado (`last-month-overspent`, ≤ 0). */
  gastadoDeMas: number;
  /** Asignado este mes (`total-budgeted`, ≤ 0 en la hoja). */
  asignado: number;
  /** Reservado para el mes que viene (`buffered-selected`). */
  reservado: number;
  /** Listo para asignar (`to-budget`). */
  total: number;
};

/** Fila del recibo: suma (+) o resta (−); el importe siempre sin signo. */
export type FilaResumen = {
  id: 'ingresos' | 'mes-pasado' | 'gastado-de-mas' | 'asignado' | 'reservado';
  suma: boolean;
  importe: number;
};

/**
 * Filas tipo recibo de «¿De dónde salen los …?» (H43). Las cifras van sin
 * signo «-» (la etiqueta ya dice si suma o resta); «Gastado de más» y
 * «Reservado» solo salen si no son cero.
 */
export function filasResumen(v: ValoresResumen): FilaResumen[] {
  const filas: FilaResumen[] = [
    { id: 'ingresos', suma: true, importe: Math.abs(v.ingresos) },
    {
      id: 'mes-pasado',
      suma: v.delMesPasado >= 0,
      importe: Math.abs(v.delMesPasado),
    },
  ];
  if (v.gastadoDeMas !== 0) {
    filas.push({
      id: 'gastado-de-mas',
      suma: false,
      importe: Math.abs(v.gastadoDeMas),
    });
  }
  filas.push({ id: 'asignado', suma: false, importe: Math.abs(v.asignado) });
  if (v.reservado !== 0) {
    filas.push({
      id: 'reservado',
      suma: false,
      importe: Math.abs(v.reservado),
    });
  }
  return filas;
}

/** Importe propuesto al abrir «Reservar» (H37): lo que queda por asignar. */
export function importeReservaInicial(listoParaAsignar: number): number {
  return Math.max(0, listoParaAsignar);
}

/** Se puede reservar si hay algo por asignar y el importe es positivo. */
export function puedeReservar(
  importe: number,
  listoParaAsignar: number,
): boolean {
  return importe > 0 && listoParaAsignar > 0;
}
